// Landing.js
import React, { useEffect, useState } from "react";
import { useData } from "contexts/DataContext";
import { fetchAllRecords } from "config/airtable";
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

const Landing = () => {
  const eventID = "reciCY3fWqNor6eH4";
  const { events, teams, loading, error } = useData();

  const [filteredJrTeams, setFilteredJrTeams] = useState([]);
  const [filteredSrTeams, setFilteredSrTeams] = useState([]);
  const [specialAwards, setSpecialAwards] = useState([]);

  // Find the current event once data loads
  const event = React.useMemo(
    () => events.find((e) => e.id === eventID),
    [events]
  );

  // 1) Filter “Junior Division” and “Senior Division” teams, then sort by lookup_rocket_score (descending)
  useEffect(() => {
    if (!eventID || teams.length === 0) return;

    const sortByScoreDesc = (a, b) => {
      const scoreA = a.data.lookup_rocket_score?.[0] ?? 0;
      const scoreB = b.data.lookup_rocket_score?.[0] ?? 0;
      return scoreB - scoreA;
    };

    const jrTeams = teams
      .filter(
        (t) =>
          t.data.fk_ext_registered_event_id?.[0] === eventID &&
          t.data.fx_team_division === "Junior Division" &&
          Array.isArray(t.data.rockets_teams) &&
          t.data.rockets_teams.length > 0
      )
      .sort(sortByScoreDesc);

    const srTeams = teams
      .filter(
        (t) =>
          t.data.fk_ext_registered_event_id?.[0] === eventID &&
          t.data.fx_team_division === "Senior Division" &&
          Array.isArray(t.data.rockets_teams) &&
          t.data.rockets_teams.length > 0
      )
      .sort(sortByScoreDesc);

    setFilteredJrTeams(jrTeams);
    setFilteredSrTeams(srTeams);
  }, [teams, eventID]);

  // 2) Fetch “special awards” for this event, then match linked team IDs to their fields
  useEffect(() => {
    if (!event || teams.length === 0) return;

    const loadSpecialAwards = async () => {
      try {
        // Use the event’s primary “Event ID” field (assuming it holds a unique string)
        const eventNameID = event.data["Event ID"];

        // Airtable formula: SEARCH the eventNameID inside {Challenge Event}
        const filterFormula = `SEARCH("${eventNameID}", {Challenge Event})`;
        const records = await fetchAllRecords("special_awards", {
          filter: filterFormula,
          sort: [{ field: "Name", direction: "asc" }],
        });

        const awards = records.map((rec) => {
          const awardName = rec.data["Name"] || "Unnamed Award";
          const teamIDs = Array.isArray(rec.data.teams) ? rec.data.teams : [];

          // For each linked team ID, pull out team_num, team_name, rollup_team_members_names
          const winningTeams = teamIDs
            .map((id) => teams.find((t) => t.id === id))
            .filter(Boolean)
            .map((t) => ({
              teamPK: t.data["Team PK"],
              teamName: t.data.team_name,
              members: t.data.rollup_team_members_names,
            }));

          return { id: rec.id, awardName, winningTeams };
        });

        setSpecialAwards(awards);
      } catch (err) {
        console.error("Error fetching special awards:", err);
      }
    };

    loadSpecialAwards();
  }, [event, teams]);

  if (loading) return <Typography>Loading event data…</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!event) return <Typography>Event not found</Typography>;

  // Format the event’s start date nicely
  const startDate = new Date(event.data["Event Start"]);
  const formattedDate = startDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  // Column definitions for each table
  const jrColumns = [
    { label: "Rank", align: "center" },
    { label: "Team #", align: "right" },
    { label: "Team Name", align: "left" },
    { label: "Members", align: "left" },
    { label: "Recorded Apogee (ft)", align: "center" },
  ];

  const srColumns = [
    { label: "Rank", align: "center" },
    { label: "Team #", align: "right" },
    { label: "Team Name", align: "left" },
    { label: "Members", align: "left" },
    { label: "Predicted Apogee (ft)", align: "center" },
    { label: "Recorded Apogee (ft)", align: "center" },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 6,
          textAlign: "center",
          backgroundImage: `url(${event.data["Event Image"]?.[0]?.url})`,
          backgroundSize: "cover",
          color: "#fff",
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
          },
        }}
      >
        <Box position="relative" zIndex={1}>
          <Typography variant="h2" component="h1" gutterBottom>
            {event.data["Event Name"]}
          </Typography>
          <Typography variant="h6" gutterBottom>
            {formattedDate} &middot; {event.data.Location}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              size="large"
              href="/rockets/register"
              disabled
              sx={{ mt: 2 }}
            >
              Registration Closed
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              href="https://www.most.org/learn/stem-challenges/"
              target="_blank"
              rel="noopener"
              sx={{ mt: 2 }}
            >
              Event Resources
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              href={event.data["participation_certificate"]?.[0]?.url}
              target="_blank"
              rel="noopener"
              sx={{ mt: 2 }}
              startIcon={<DownloadIcon />}
              download
            >
              Download Certificate
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* --- Special Awards Section --- */}
      {specialAwards.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 6 }}>
          <Typography
            variant="h4"
            gutterBottom
            textAlign="center"
            fontWeight="bold"
          >
            Special Awards
          </Typography>
          {specialAwards.map(({ id, awardName, winningTeams }) => (
            <Box key={id} sx={{ mb: 3, textAlign: "center" }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {awardName}
              </Typography>
              {winningTeams.length > 0 ? (
                winningTeams.map((team, idx) => (
                  <Typography key={idx}>
                    <strong>{team.teamPK}</strong> &mdash; {team.members}
                  </Typography>
                ))
              ) : (
                <Typography>— No team assigned —</Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {/* --- Ranked Awards Section --- */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          fontWeight="bold"
        >
          Ranked Awards
        </Typography>
      </Box>

      {/* --- Junior Division Teams Table --- */}
      <Typography variant="h5" gutterBottom>
        Junior Division Teams
      </Typography>
      <Paper elevation={1} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              {jrColumns.map((col) => (
                <TableCell
                  key={col.label}
                  align={col.align}
                  sx={{ fontWeight: "bold", color: "common.white" }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredJrTeams.map((team, idx) => {
              const td = team.data;
              const recordedApo = td.lookup_recorded_apogee?.[0];
              return (
                <TableRow
                  key={team.id}
                  sx={
                    td.withdrawn
                      ? {
                          "& .MuiTableCell-root": {
                            textDecoration: "line-through",
                            color: "text.disabled",
                          },
                        }
                      : {}
                  }
                >
                  <TableCell align="center">{idx + 1}</TableCell>
                  <TableCell align="right">{td.team_num}</TableCell>
                  <TableCell align="left">{td.team_name}</TableCell>
                  <TableCell align="left">
                    {td.rollup_team_members_names}
                  </TableCell>
                  <TableCell align="center">
                    {recordedApo != null ? Math.round(recordedApo) : ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* --- Senior Division Teams Table --- */}
      <Typography variant="h5" gutterBottom>
        Senior Division Teams
      </Typography>
      <Paper elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              {srColumns.map((col) => (
                <TableCell
                  key={col.label}
                  align={col.align}
                  sx={{ fontWeight: "bold", color: "common.white" }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSrTeams.map((team, idx) => {
              const td = team.data;
              const predApo = td.lookup_predicted_apogee?.[0];
              const recApo = td.lookup_recorded_apogee?.[0];
              return (
                <TableRow
                  key={team.id}
                  sx={
                    td.withdrawn
                      ? {
                          "& .MuiTableCell-root": {
                            textDecoration: "line-through",
                            color: "text.disabled",
                          },
                        }
                      : {}
                  }
                >
                  <TableCell align="center">{idx + 1}</TableCell>
                  <TableCell align="right">{td.team_num}</TableCell>
                  <TableCell align="left">{td.team_name}</TableCell>
                  <TableCell align="left">
                    {td.rollup_team_members_names}
                  </TableCell>
                  <TableCell align="center">
                    {predApo != null ? Math.round(predApo) : ""}
                  </TableCell>
                  <TableCell align="center">
                    {recApo != null ? Math.round(recApo) : ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
};

export default Landing;
